import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import { recipeApiService } from "services/recipeApiService";
// import RecipeDetailsModal from "components/Modals/RecipeDetailsModal";
// import UpdateRecipeModal from "components/Modals/UpdateRecipeModal";
import DeleteModal from "pages/Calendar/DeleteModal";

const AllRecipes = () => {
  const navigate = useNavigate();

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [deletingRecipe, setDeletingRecipe] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch recipes
  const fetchRecipes = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await recipeApiService.getAllRecipes();
      setRecipes(data);
    } catch (error) {
      toast.error("Failed to fetch recipes");
      console.error("Error fetching recipes:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedRecipeId(id);
    setDeleteModal(true);
  };

  const handleDeleteRecipe = async () => {
    setDeletingRecipe(true);
    try {
      await recipeApiService.deleteRecipe(selectedRecipeId);
      toast.success("Recipe deleted successfully");
      setDeleteModal(false);
      fetchRecipes(false);
    } catch (error) {
      toast.error("Failed to delete recipe");
      console.error("Error deleting recipe:", error);
    } finally {
      setDeletingRecipe(false);
    }
  };

  const handleShowDetails = (recipe) => {
    setSelectedRecipe(recipe);
    setDetailsModalOpen(true);
  };

  const handleUpdateRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchRecipes(false);
    toast.success("Recipe updated successfully");
  };

  // Filter recipes
  const filteredRecipes = useMemo(() => {
    if (!recipes.length) return [];

    if (!nameFilter) return recipes;

    return recipes.filter(recipe =>
      recipe.recipe_name.toLowerCase().includes(nameFilter.toLowerCase())
    );
  }, [recipes, nameFilter]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-BD');
    } catch {
      return dateString;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Recipe ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">#{row.original.id}</div>
            {/* <small className="text-muted">
              {formatDate(row.original.systemdate)}
            </small> */}
          </div>
        ),
      },
      {
        header: "Recipe Name",
        accessorKey: "recipe_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.recipe_name}</div>
          </div>
        ),
      },
      {
        header: "Ingredients",
        accessorKey: "ingredient_details",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const ingredients = row.original.ingredient_details || [];
          return (
            <div className="max-w-xs d-flex gap-5">
              {ingredients.map((ingredient, index) => (
                <div key={index} className="mb-2">
                  {/* <small>
                    {index + 1}. {ingredient.ingredient_name}
                    <span className="text-primary"> ({ingredient.recipe_quantity}{ingredient.unit})</span>
                  </small> */}

                  Store Name: {ingredient.store_name}
                  <br />
                  Ingredient Name: {ingredient.ingredient_name}
                  <br />
                  Recipe Quantity: {ingredient.recipe_quantity}{ingredient.unit}
                  <br />
                  Unit Price: {ingredient.unitprice.toFixed(2)}
                  <br />
                  Ingredient Cost: {ingredient.ingredient_cost}

                </div>
              ))}
              {/* {ingredients.length > 2 && (
                <small className="text-muted">
                  +{ingredients.length - 2} more ingredients
                </small>
              )} */}
            </div>
          );
        },
      },
      {
        header: "Recipe Cost",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{formatCurrency(row.original.recipe_cost)}</div>
            {/* <small className="text-muted">
              Portion: {row.original.portion_total} ({row.original.portion_unit})
            </small> */}
          </div>
        ),
      },
      {
        header: "Portion Total",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            {row.original.portion_total || "-"}
          </div>
        ),
      },
      {
        header: "Portion Unit",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            {row.original.portion_unit || "-"}
          </div>
        ),
      },
      {
        header: "Menu Price",
        accessorKey: "menu_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.menu_price ? formatCurrency(row.original.menu_price) : "-"}
          </div>
        ),
      },
      {
        header: "Note",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            {row.original.note || "-"}
          </div>
        ),
      },

      // {
      //   header: "Q Factor",
      //   accessorKey: "q_factor",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div>
      //       <span className={`badge ${row.original.q_factor ? 'bg-info' : 'bg-secondary'}`}>
      //         {row.original.q_factor || "N/A"}
      //       </span>
      //     </div>
      //   ),
      // },
      {
        header: "Item Image",
        accessorKey: "item_image",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            {row.original.item_image ? (
              <button
                onClick={() => setSelectedImage(row.original.item_image)}
                className="btn btn-info btn-sm"
                title="View Image"
              >
                <i className="fas fa-eye"></i>
              </button>
            ) : (
              <span className="text-muted">No image</span>
            )}
          </div>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-1">


              <button
                onClick={() => navigate(`/create-recipe/${row.original.id}`)}
                className="btn btn-warning btn-sm"
                title="Update Recipe"
              >
                <i className="fas fa-edit"></i>
              </button>

              <button
                className="btn btn-danger btn-sm"
                onClick={() => showDeleteModal(row.original.id)}
                title="Delete Recipe"
                disabled={deletingRecipe}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        ),
      },
    ],
    [deletingRecipe]
  );

  // Calculate statistics
  const totalRecipes = recipes.length;
  const totalRecipeCost = recipes.reduce((sum, recipe) => sum + parseFloat(recipe.recipe_cost || 0), 0);
  const avgMenuPrice = recipes.length > 0
    ? recipes.reduce((sum, recipe) => sum + parseFloat(recipe.menu_price || 0), 0) / recipes.length
    : 0;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Recipes" breadcrumbItem="All Recipes" />

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">

                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: "300px" }}
                    placeholder="Recipe name..."
                    value={nameFilter}
                    onChange={e => setNameFilter(e.target.value)}
                  />
                </div>



                <button
                  className="btn btn-light"
                  onClick={() => setNameFilter("")}
                >
                  <i className="fas fa-times me-1"></i> Clear Filter
                </button>

              </div>




              <div className="d-flex gap-2">
                <button
                  onClick={() => navigate("/create-recipe")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create New Recipe
                </button>


                <button
                  className="btn btn-light"
                  onClick={() => fetchRecipes(false)}
                  disabled={refreshing}
                  title="Refresh Recipes"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>


              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <TableContainer
          columns={columns}
          data={filteredRecipes}
          isGlobalFilter={false}
          isPagination={true}
          SearchPlaceholder="Search recipes, ingredients..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          isCustomPageSize={true}
        />

        {/* Delete Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteRecipe}
          loader={deletingRecipe}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Recipe"
          message="Are you sure you want to delete this recipe? This action cannot be undone."
        />

        {/* Image Viewer Modal */}
        {selectedImage && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedImage(null)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body p-0 text-center">
                  <img
                    src={selectedImage}
                    alt="Recipe"
                    className="img-fluid"
                    style={{ maxHeight: '70vh', objectFit: 'contain' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllRecipes;